import React, { useState, useRef } from 'react';
import './ImportLocalFilesModal.css';

interface ImportLocalFilesModalProps {
    /** Passed in from the editor, which is where the language hook lives */
    t: any;
    onClose: () => void;
    onImport: (files: File[]) => Promise<void>;
}

interface FileWithPreview extends File {
    preview?: string;
    status?: 'pending' | 'uploading' | 'success' | 'error';
    error?: string;
}

export const ImportLocalFilesModal: React.FC<ImportLocalFilesModalProps> = ({
    t,
    onClose,
    onImport,
}) => {
    const [selectedFiles, setSelectedFiles] = useState<FileWithPreview[]>([]);
    const [isImporting, setIsImporting] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const supportedFormats = [
        '.docx',
        '.doc',
        '.txt',
        '.rtf',
        '.pdf',
        '.html',
        '.htm',
        '.md',
        '.odt',
    ];

    const handleFileSelect = (files: FileList | null) => {
        if (!files) return;

        const newFiles = Array.from(files).map(file => {
            const fileWithPreview = file as FileWithPreview;
            fileWithPreview.status = 'pending';
            return fileWithPreview;
        });

        setSelectedFiles(prev => [...prev, ...newFiles]);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        handleFileSelect(e.dataTransfer.files);
    };

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const removeFile = (index: number) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleImport = async () => {
        setIsImporting(true);

        try {
            // Update all files to uploading status
            setSelectedFiles(prev =>
                prev.map(f => ({ ...f, status: 'uploading' as const }))
            );

            await onImport(selectedFiles);

            // Update all to success
            setSelectedFiles(prev =>
                prev.map(f => ({ ...f, status: 'success' as const }))
            );

            // Close after a brief delay to show success
            setTimeout(() => {
                onClose();
            }, 1000);
        } catch (error) {
            console.error('Import failed:', error);
            setSelectedFiles(prev =>
                prev.map(f => ({
                    ...f,
                    status: 'error' as const,
                    error: error instanceof Error ? error.message : t.importDocs.importFailed,
                }))
            );
        } finally {
            setIsImporting(false);
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="import-modal" onClick={(e) => e.stopPropagation()}>
                <div className="import-modal-header">
                    <h2>{t.importDocs.localTitle}</h2>
                    <button className="close-btn" onClick={onClose}>✕</button>
                </div>

                <div className="import-modal-content">
                    <div
                        className={`file-dropzone ${dragActive ? 'drag-active' : ''}`}
                        onDrop={handleDrop}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept={supportedFormats.join(',')}
                            onChange={(e) => handleFileSelect(e.target.files)}
                            style={{ display: 'none' }}
                        />
                        <div className="dropzone-content">
                            <div className="dropzone-icon">📁</div>
                            <p className="dropzone-title">
                                {t.importDocs.dropzoneTitle}
                            </p>
                            <p className="dropzone-subtitle">
                                {t.importDocs.supportedFormats} {supportedFormats.join(', ')}
                            </p>
                        </div>
                    </div>

                    {selectedFiles.length > 0 && (
                        <div className="selected-files-list">
                            <h3>{t.importDocs.selectedFiles} ({selectedFiles.length})</h3>
                            <div className="files-container">
                                {selectedFiles.map((file, index) => (
                                    <div key={index} className={`file-item ${file.status}`}>
                                        <div className="file-info">
                                            <div className="file-icon">
                                                {file.status === 'success' && '✓'}
                                                {file.status === 'error' && '✕'}
                                                {file.status === 'uploading' && '⏳'}
                                                {file.status === 'pending' && '📄'}
                                            </div>
                                            <div className="file-details">
                                                <div className="file-name">{file.name}</div>
                                                <div className="file-size">
                                                    {formatFileSize(file.size)}
                                                </div>
                                                {file.error && (
                                                    <div className="file-error">{file.error}</div>
                                                )}
                                            </div>
                                        </div>
                                        {file.status === 'pending' && !isImporting && (
                                            <button
                                                className="remove-file-btn"
                                                onClick={() => removeFile(index)}
                                            >
                                                ✕
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="import-modal-footer">
                    <button
                        className="modal-btn secondary"
                        onClick={onClose}
                        disabled={isImporting}
                    >
                        {t.common.cancel}
                    </button>
                    <button
                        className="modal-btn primary"
                        onClick={handleImport}
                        disabled={selectedFiles.length === 0 || isImporting}
                    >
                        {isImporting
                            ? t.importDocs.importing
                            : (selectedFiles.length === 1
                                ? t.importDocs.importFileOne
                                : t.importDocs.importFileMany
                              ).replace('{count}', String(selectedFiles.length))}
                    </button>
                </div>
            </div>
        </div>
    );
};
